import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { SITE_STATUS, POSITIONS, ACTION_LABELS } from '../lib/status';
import { LayoutGrid, List, MapPin, ExternalLink, X, Search, ChevronDown, Camera, Building2, Megaphone, Users, Layers, ClipboardList, UserCircle, ChevronsDownUp, ChevronsUpDown, Download, FileText, FileSpreadsheet, FileDown, TriangleAlert, SlidersHorizontal, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DIMS = {
  society: { label: 'Society', icon: Building2, key: (p) => p.society_name || 'Unknown society' },
  lift: { label: 'Tower & Lift', icon: Layers, key: (p) => `${p.tower_name || '—'} · ${p.lift_name || '—'}` },
  lift_only: { label: 'Lift', icon: Layers, key: (p) => p.lift_name || '—' },
  site_type: { label: 'Site Position', icon: LayoutGrid, key: (p) => POSITIONS[p.position_type] || 'Other' },
  campaign: { label: 'Campaign', icon: Megaphone, key: (p) => p.campaign_name || 'No campaign' },
  client: { label: 'Client', icon: Users, key: (p) => p.client_name || 'No client' },
  inspection: { label: 'Inspection Job', icon: ClipboardList, key: (p) => (p.inspection_id ? `Job ${String(p.inspection_id).slice(0, 8)}` : 'Ad-hoc capture') },
  action: { label: 'Action Type', icon: Camera, key: (p) => ACTION_LABELS[p.action_type] || p.action_type },
  operator: { label: 'Operator', icon: UserCircle, key: (p) => p.operator_name || 'Unknown' },
};
const NESTED_DIMS = {
  society_tower: { label: 'Society › Tower', icon: Building2, l1: (p) => p.society_name || 'Unknown society', l2: (p) => p.tower_name || '—' },
  tower_lift: { label: 'Tower › Lift', icon: Layers, l1: (p) => p.tower_name || '—', l2: (p) => p.lift_name || '—' },
};
const ALL_DIMS = { ...DIMS, ...NESTED_DIMS };
const STATUS_KEYS = ['not_installed', 'installed', 'inspected_ok', 'damaged', 'repair_pending', 'repaired', 'replaced'];
const ACTION_OPTS = ['install', 'inspect_ok', 'damage', 'needs_repair', 'repair', 'replace'];
const ts = (v) => (v || '').slice(0, 19).replace('T', ' ');
const gpsStr = (p) => (p.gps?.lat != null ? `${p.gps.lat.toFixed(5)}, ${p.gps.lng.toFixed(5)}` : '');

const download = (blob, name) => { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url); };
const toReportRow = (p) => ({
  site_code: String(p.site_id || '').slice(0, 8), society: p.society_name || '', tower: p.tower_name || '', lift: p.lift_name || '',
  position: POSITIONS[p.position_type] || p.position_type || '', status: p.new_status || '', campaign: p.campaign_name || '-',
  last_action: ACTION_LABELS[p.action_type] || p.action_type || '-', timestamp: ts(p.timestamp), address: p.address || gpsStr(p) || '-',
});
const CSV_COLS = ['Site', 'Society', 'Tower', 'Lift', 'Position', 'Status', 'Action', 'Operator', 'Timestamp', 'GPS', 'Address', 'Notes'];
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const exportCsv = (list, title) => {
  const rows = list.map((p) => [String(p.site_id || '').slice(0, 8), p.society_name, p.tower_name, p.lift_name, POSITIONS[p.position_type] || p.position_type, (SITE_STATUS[p.new_status] || {}).label || p.new_status, ACTION_LABELS[p.action_type] || p.action_type, p.operator_name, ts(p.timestamp), gpsStr(p), p.address, p.notes]);
  const csv = [CSV_COLS, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
  download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${title}.csv`);
};
const exportServer = async (list, title, fmt) => {
  try {
    const res = await api.post(`/reports/proofs-export?fmt=${fmt}`, { title, meta: `${list.length} records`, rows: list.map(toReportRow) }, { responseType: 'blob' });
    download(res.data, `${title}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`);
  } catch { toast.error('Export failed'); }
};

function ExportMenu({ list, title, testid }) {
  const [busy, setBusy] = useState(false);
  const server = async (fmt) => { setBusy(true); await exportServer(list, title, fmt); setBusy(false); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="secondary" disabled={busy} data-testid={testid} onClick={(e) => e.stopPropagation()}>{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} Export</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card">
        <DropdownMenuItem data-testid={`${testid}-csv`} onClick={(e) => { e.stopPropagation(); exportCsv(list, title); }}><FileDown className="mr-2 h-4 w-4" /> CSV</DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-excel`} onClick={(e) => { e.stopPropagation(); server('excel'); }}><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel</DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-pdf`} onClick={(e) => { e.stopPropagation(); server('pdf'); }}><FileText className="mr-2 h-4 w-4" /> PDF (damage report)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function GroupedProofGallery({ proofs = [], allowedGroupings = ['society', 'lift', 'site_type', 'campaign', 'action'], initialGroupBy, emptyMessage = 'No proof photos yet.' }) {
  const dims = allowedGroupings.filter((k) => ALL_DIMS[k]);
  const [groupBy, setGroupBy] = useState(initialGroupBy && ALL_DIMS[initialGroupBy] ? initialGroupBy : dims[0]);
  const [q, setQ] = useState('');
  const [view, setView] = useState('grid');
  const [active, setActive] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [statusSet, setStatusSet] = useState(() => new Set());
  const [action, setAction] = useState('all');
  const [position, setPosition] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const toggleStatus = (s) => setStatusSet((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const damagedActive = statusSet.size === 2 && statusSet.has('damaged') && statusSet.has('repair_pending');
  const toggleDamaged = () => setStatusSet(damagedActive ? new Set() : new Set(['damaged', 'repair_pending']));
  const clearFilters = () => { setStatusSet(new Set()); setAction('all'); setPosition('all'); setDateFrom(''); setDateTo(''); setQ(''); };
  const activeFilterCount = statusSet.size + (action !== 'all' ? 1 : 0) + (position !== 'all' ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return proofs.filter((p) => {
      if (s && ![p.society_name, p.tower_name, p.lift_name, p.campaign_name, p.client_name, p.operator_name, p.notes, POSITIONS[p.position_type], ACTION_LABELS[p.action_type]].filter(Boolean).some((x) => String(x).toLowerCase().includes(s))) return false;
      if (statusSet.size && !statusSet.has(p.new_status)) return false;
      if (action !== 'all' && p.action_type !== action) return false;
      if (position !== 'all' && p.position_type !== position) return false;
      const d = (p.timestamp || '').slice(0, 10);
      if (dateFrom && d && d < dateFrom) return false;
      if (dateTo && d && d > dateTo) return false;
      return true;
    });
  }, [proofs, q, statusSet, action, position, dateFrom, dateTo]);

  const nested = NESTED_DIMS[groupBy] || null;
  const groups = useMemo(() => {
    if (nested) return [];
    const keyFn = DIMS[groupBy].key;
    const m = {};
    filtered.forEach((p) => { const k = keyFn(p); (m[k] = m[k] || []).push(p); });
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
  }, [filtered, groupBy, nested]);
  const nestedGroups = useMemo(() => {
    if (!nested) return [];
    const m = {};
    filtered.forEach((p) => { const a = nested.l1(p); const b = nested.l2(p); ((m[a] = m[a] || {})[b] = m[a][b] || []).push(p); });
    return Object.entries(m)
      .map(([l1, l2m]) => [l1, Object.entries(l2m).sort((x, y) => y[1].length - x[1].length)])
      .sort((a, b) => b[1].reduce((s, [, l]) => s + l.length, 0) - a[1].reduce((s, [, l]) => s + l.length, 0));
  }, [filtered, groupBy, nested]);

  const groupKeys = nested ? nestedGroups.map(([k]) => k) : groups.map(([k]) => k);
  const allCollapsed = groupKeys.length > 0 && groupKeys.every((k) => collapsed[k]);
  const toggleAll = () => { const next = {}; if (!allCollapsed) groupKeys.forEach((k) => { next[k] = true; }); setCollapsed(next); };

  return (
    <div className="space-y-3" data-testid="grouped-gallery-container">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={groupBy} onValueChange={(v) => { setGroupBy(v); setCollapsed({}); }}>
          <SelectTrigger data-testid="group-by-selector" className="w-52 bg-card"><SelectValue /></SelectTrigger>
          <SelectContent>
            {dims.map((k) => { const Icon = ALL_DIMS[k].icon; return <SelectItem key={k} value={k}><span className="flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> Group by {ALL_DIMS[k].label}</span></SelectItem>; })}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="gallery-search" placeholder="Search operator, tower, notes…" value={q} onChange={(e) => setQ(e.target.value)} className="w-52 pl-8" />
        </div>
        <Button variant={showFilters ? 'default' : 'secondary'} size="sm" data-testid="gallery-filters-toggle" onClick={() => setShowFilters((v) => !v)}><SlidersHorizontal className="mr-1 h-4 w-4" /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}</Button>
        <Button variant={damagedActive ? 'default' : 'secondary'} size="sm" data-testid="gallery-damaged-toggle" onClick={toggleDamaged} className={damagedActive ? 'bg-rose-600 hover:bg-rose-500' : ''}><TriangleAlert className="mr-1 h-4 w-4" /> Damaged only</Button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{filtered.length} photos · {nested ? nestedGroups.length : groups.length} groups</span>
          <ExportMenu list={filtered} title="All_filtered_proofs" testid="gallery-export-all" />
          <Button variant="ghost" size="sm" data-testid="gallery-collapse-all" onClick={toggleAll} className="text-xs">{allCollapsed ? <ChevronsUpDown className="h-4 w-4" /> : <ChevronsDownUp className="h-4 w-4" />}</Button>
          <div className="flex rounded-lg border border-border p-0.5">
            <button data-testid="gallery-view-grid" onClick={() => setView('grid')} className={`rounded px-2.5 py-1.5 ${view === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}><LayoutGrid className="h-4 w-4" /></button>
            <button data-testid="gallery-view-list" onClick={() => setView('list')} className={`rounded px-2.5 py-1.5 ${view === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}><List className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="space-y-3 rounded-xl border border-border bg-card p-3" data-testid="gallery-filter-panel">
          <div>
            <p className="overline mb-1.5 text-muted-foreground">Status</p>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_KEYS.map((s) => { const c = SITE_STATUS[s] || {}; const on = statusSet.has(s); return (
                <button key={s} data-testid={`filter-status-${s}`} onClick={() => toggleStatus(s)} className="flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] transition" style={{ color: on ? '#fff' : c.dot, borderColor: c.dot, background: on ? c.dot : 'transparent' }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: on ? '#fff' : c.dot }} />{c.label}
                </button>
              ); })}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div><p className="overline mb-1 text-muted-foreground">Action</p><Select value={action} onValueChange={setAction}><SelectTrigger data-testid="filter-action" className="w-40 bg-background"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All actions</SelectItem>{ACTION_OPTS.map((a) => <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>)}</SelectContent></Select></div>
            <div><p className="overline mb-1 text-muted-foreground">Position</p><Select value={position} onValueChange={setPosition}><SelectTrigger data-testid="filter-position" className="w-40 bg-background"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All positions</SelectItem>{Object.entries(POSITIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div><p className="overline mb-1 text-muted-foreground">From</p><Input type="date" data-testid="filter-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 bg-background" /></div>
            <div><p className="overline mb-1 text-muted-foreground">To</p><Input type="date" data-testid="filter-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 bg-background" /></div>
            <Button variant="ghost" size="sm" data-testid="filter-clear" onClick={clearFilters}>Clear all</Button>
          </div>
        </div>
      )}

      {(nested ? nestedGroups.length === 0 : groups.length === 0) ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground"><Camera className="mx-auto mb-2 h-8 w-8" />{emptyMessage}</div>
      ) : nested ? (
        <div className="space-y-3">
          {nestedGroups.map(([l1, l2list]) => {
            const g1 = l1.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
            const l1Collapsed = !!collapsed[l1];
            const flat = l2list.flatMap(([, items]) => items);
            return (
              <div key={l1} data-testid={`group-section-${g1}`} className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <button data-testid={`group-toggle-${g1}`} onClick={() => setCollapsed((c) => ({ ...c, [l1]: !c[l1] }))} className="flex flex-1 items-center gap-2 text-left">
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${l1Collapsed ? '-rotate-90' : ''}`} />
                    <span className="font-head text-lg font-bold">{l1}</span>
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">{flat.length}</span>
                  </button>
                  <ExportMenu list={flat} title={`${nested.label}_${l1}`} testid={`group-export-${g1}`} />
                </div>
                {!l1Collapsed && (
                  <div className="space-y-2 px-3 pb-3">
                    {l2list.map(([l2, items]) => {
                      const k2 = `${l1}|||${l2}`;
                      const g2 = `${g1}-${l2.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
                      const l2Collapsed = !!collapsed[k2];
                      return (
                        <div key={k2} data-testid={`group-section-${g2}`} className="overflow-hidden rounded-lg border border-border/70 bg-background/40">
                          <button data-testid={`group-toggle-${g2}`} onClick={() => setCollapsed((c) => ({ ...c, [k2]: !c[k2] }))} className="flex w-full items-center gap-2 px-3 py-2 text-left">
                            <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${l2Collapsed ? '-rotate-90' : ''}`} />
                            <span className="text-sm font-semibold">{l2}</span>
                            <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold">{items.length}</span>
                          </button>
                          {!l2Collapsed && <ProofItems list={items} view={view} onOpen={setActive} />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(([key, list]) => {
            const gkey = key.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
            const isCollapsed = !!collapsed[key];
            const byStatus = {};
            list.forEach((p) => { byStatus[p.new_status] = (byStatus[p.new_status] || 0) + 1; });
            return (
              <div key={key} data-testid={`group-section-${gkey}`} className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <button data-testid={`group-toggle-${gkey}`} onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))} className="flex flex-1 items-center gap-2 text-left">
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                    <span className="font-head text-lg font-bold">{key}</span>
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">{list.length}</span>
                  </button>
                  <div className="hidden gap-1.5 md:flex">
                    {Object.entries(byStatus).map(([st, n]) => { const c = SITE_STATUS[st] || {}; return <span key={st} className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px]" style={{ color: c.dot, borderColor: c.dot }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: c.dot }} />{n}</span>; })}
                  </div>
                  <ExportMenu list={list} title={`${DIMS[groupBy].label}_${key}`} testid={`group-export-${gkey}`} />
                </div>

                {!isCollapsed && <ProofItems list={list} view={view} onOpen={setActive} />}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="proof-lightbox-modal">
          {active && (
            <div className="space-y-3">
              <DialogTitle className="sr-only">Proof detail</DialogTitle>
              <DialogDescription className="sr-only">Proof photos and capture metadata</DialogDescription>
              <div className="flex items-center justify-between">
                <div><p className="font-head text-xl font-bold">{ACTION_LABELS[active.action_type]} · {POSITIONS[active.position_type]}</p><p className="text-xs text-muted-foreground">{active.society_name} · {active.tower_name} · {active.lift_name}</p></div>
                <button onClick={() => setActive(null)}><X className="h-5 w-5" /></button>
              </div>
              <div className="flex gap-2 overflow-x-auto">{active.file_ids.map((f) => <img key={f} src={fileUrl(f)} alt="" className="max-h-80 rounded-lg object-contain" />)}</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {active.campaign_name && <Detail label="Campaign" value={active.campaign_name} />}
                {active.client_name && <Detail label="Client" value={active.client_name} />}
                <Detail label="Operator" value={active.operator_name} />
                <Detail label="Timestamp" value={ts(active.timestamp)} />
                {active.gps?.lat && <div><p className="overline text-muted-foreground">GPS</p><a target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${active.gps.lat},${active.gps.lng}`} className="flex items-center gap-1 font-mono text-xs text-primary hover:underline">{active.gps.lat.toFixed(5)}, {active.gps.lng.toFixed(5)} <ExternalLink className="h-3 w-3" /></a></div>}
              </div>
              {active.address && <div><Detail label="Address" value={active.address} /><p className="text-[10px] text-muted-foreground">Address data © OpenStreetMap contributors</p></div>}
              {active.notes && <Detail label="Notes" value={active.notes} />}
              <Link to={`/sites/${active.site_id}`}><Button className="w-full" variant="secondary">Open full site history</Button></Link>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProofItems({ list, view, onOpen }) {
  return view === 'grid' ? (
    <div className="grid grid-cols-2 gap-2 p-3 pt-0 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {list.map((p) => { const c = SITE_STATUS[p.new_status] || {}; return (
        <button key={p.id} data-testid={`proof-thumbnail-${p.id}`} onClick={() => onOpen(p)} className="tile-press group relative overflow-hidden rounded-lg border border-border text-left">
          <img src={fileUrl(p.file_ids[0])} alt="" className="h-36 w-full object-cover" />
          <span className="absolute left-1.5 top-1.5 rounded border px-1 py-0.5 text-[9px] font-mono uppercase" style={{ background: 'rgba(0,0,0,0.65)', color: c.dot, borderColor: c.dot }}>{ACTION_LABELS[p.action_type]}</span>
          <div className="bg-card p-1.5">
            <p className="truncate text-[11px] font-medium">{POSITIONS[p.position_type]}</p>
            <p className="truncate text-[9px] text-muted-foreground">{p.tower_name} · {p.lift_name}</p>
            <p className="mt-0.5 font-mono text-[9px] text-muted-foreground">{ts(p.timestamp).slice(0, 16)}</p>
          </div>
        </button>
      ); })}
    </div>
  ) : (
    <div className="space-y-2 p-3 pt-0">
      {list.map((p) => { const c = SITE_STATUS[p.new_status] || {}; return (
        <div key={p.id} data-testid={`proof-row-${p.id}`} className="flex gap-3 rounded-lg border border-border bg-background/50 p-2.5">
          <button onClick={() => onOpen(p)} className="shrink-0"><img src={fileUrl(p.file_ids[0])} alt="" className="h-16 w-16 rounded-md object-cover" /></button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2"><span className="rounded border px-1.5 py-0.5 text-[10px] font-mono uppercase" style={{ color: c.dot, borderColor: c.dot }}>{ACTION_LABELS[p.action_type]}</span><span className="text-sm font-medium">{POSITIONS[p.position_type]}</span></div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.society_name} · {p.tower_name} · {p.lift_name}{p.campaign_name ? ` · ${p.campaign_name}` : ''}</p>
            <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{ts(p.timestamp)} · {p.operator_name}</p>
          </div>
          <Link to={`/sites/${p.site_id}`} className="self-center text-xs text-primary hover:underline">Site</Link>
        </div>
      ); })}
    </div>
  );
}

function Detail({ label, value }) {
  return <div><p className="overline text-muted-foreground">{label}</p><p className="text-sm">{value}</p></div>;
}
